import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Icon } from './ui/Icon.js';

export interface WasteSavingsResponse {
  totalSavedG: number;
  totalSavedLb: number;
  cookedPlanCount: number;
  perPlan: Array<{
    mealPlanId: string | null;
    cookedAt: string;
    recipeNames: string[];
    savedG: number;
  }>;
  perIngredient: Array<{
    ingredientName: string;
    savedG: number;
    baselineQtyG: number;
  }>;
}

interface WasteBarRowProps {
  ingredientName: string;
  savedG: number;
  baselineQtyG: number;
}

export function WasteBarRow({ ingredientName, savedG, baselineQtyG }: WasteBarRowProps) {
  const pct = baselineQtyG > 0 ? Math.min(100, (savedG / baselineQtyG) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5" style={{ minHeight: 28 }}>
      <span
        className="text-sm flex-shrink-0 capitalize"
        style={{ color: 'var(--ink-2)', width: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {ingredientName}
      </span>
      <div
        className="flex-1 rounded-full overflow-hidden"
        style={{ background: 'var(--surface-2)', height: 10, border: '1px solid var(--line-soft)' }}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: 'var(--accent)' }}
        />
      </div>
      <span className="text-xs font-semibold flex-shrink-0 tabular-nums" style={{ color: 'var(--ink-3)', width: 56, textAlign: 'right' }}>
        {Math.round(savedG)}g
      </span>
    </div>
  );
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded ${className}`} style={{ background: 'var(--surface-2)' }} />;
}

interface WasteSavingsWidgetProps {
  /** Show all ingredients, not just top 20. Used by /savings page. */
  showAll?: boolean;
}

export function WasteSavingsWidget({ showAll = false }: WasteSavingsWidgetProps = {}) {
  const { data, isLoading } = useQuery<WasteSavingsResponse>({
    queryKey: ['waste-savings'],
    queryFn: () => api.get('/api/me/waste-savings'),
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <div className="card p-6">
        <Skeleton className="h-6 w-48 mb-3" />
        <Skeleton className="h-12 w-40 mb-4" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (!data || data.totalSavedLb === 0 || data.perIngredient.length === 0) {
    return (
      <div className="card p-6">
        <div className="text-xs font-semibold uppercase tracking-wider mb-2"
          style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
          <Icon name="leaf" size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -1 }} />
          Food waste saved
        </div>
        <h3 className="h-serif" style={{ fontSize: 22, margin: 0, color: 'var(--ink)', marginBottom: 8 }}>
          Your savings start with your first cooked plan
        </h3>
        <p className="text-sm mb-3" style={{ color: 'var(--ink-3)', lineHeight: 1.5 }}>
          When you mark a recipe as cooked, we calculate how much food waste you
          avoided compared to buying ingredients ad-hoc.
        </p>
        <Link to="/methodology#waste-savings" className="text-sm font-semibold inline-flex items-center gap-1 hover:opacity-80"
          style={{ color: 'var(--accent)' }}>
          How we calculate this <Icon name="chev" size={13} />
        </Link>
      </div>
    );
  }

  const ingredients = showAll ? data.perIngredient : data.perIngredient.slice(0, 20);

  return (
    <div className="card p-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
            <Icon name="leaf" size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -1 }} />
            Food waste saved
          </div>
          <div className="h-display" style={{ fontSize: 44, lineHeight: 1, color: 'var(--accent)' }}>
            {data.totalSavedLb} lb saved
          </div>
          <div className="text-sm mt-1.5" style={{ color: 'var(--ink-3)' }}>
            across {data.cookedPlanCount} plan{data.cookedPlanCount === 1 ? '' : 's'}
          </div>
        </div>
        <Link to="/methodology#waste-savings" className="text-sm font-medium flex items-center gap-1 hover:opacity-80"
          style={{ color: 'var(--ink-3)' }}>
          How we calculate this <Icon name="chev" size={13} />
        </Link>
      </div>

      <div className="flex flex-col" style={{ marginTop: 16 }}>
        {ingredients.map((ing) => (
          <WasteBarRow
            key={ing.ingredientName}
            ingredientName={ing.ingredientName}
            savedG={ing.savedG}
            baselineQtyG={ing.baselineQtyG}
          />
        ))}
      </div>

      {!showAll && data.perIngredient.length > 0 && (
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--line-soft)' }}>
          <Link to="/savings" className="text-sm font-semibold flex items-center gap-1 hover:opacity-80"
            style={{ color: 'var(--accent)' }}>
            See full breakdown <Icon name="chev" size={13} />
          </Link>
        </div>
      )}
    </div>
  );
}
